import { useState, useEffect, useMemo, useRef } from "react";
import { 
  Plus, Calendar, CheckCircle, Clock, Edit, Trash2, Filter, AlertTriangle, 
  User, Search, Layers, List, LayoutGrid, ChevronDown, ChevronRight,
  Flag, Target, Users, Circle, MoreVertical, GripVertical, Briefcase, 
  ArrowUpDown, ArrowUp, ArrowDown, Eye, X, ThumbsUp, ThumbsDown,
  MessageSquare, Send, Flame
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { format, isToday, isPast, parseISO, isTomorrow, addDays, isYesterday, isThisWeek, isThisMonth, isSameWeek, isSameMonth, subWeeks, subMonths } from "date-fns";
import { useBusiness } from "@/contexts/BusinessContext";
import MobileTasksA from "@/components/mobile/MobileTasksA";
import TaskFormBody from "@/components/tasks/TaskFormBody";
import TasksCalendarView from "@/components/tasks/TasksCalendarView";
import ServicesDialog from "@/pages/work-planner/monthly/ServicesDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Role display config
const ROLE_LABELS = {
  admin: "Admin",
  manager: "Manager",
  project_manager: "PM",
  team_leader: "TL",
  employee: "Employee",
  user: "Employee"  // Legacy support
};

const ROLE_COLORS = {
  admin: "bg-purple-100 text-purple-700",
  manager: "bg-indigo-100 text-indigo-700",
  project_manager: "bg-blue-100 text-blue-700",
  team_leader: "bg-cyan-100 text-cyan-700",
  employee: "bg-green-100 text-green-700",
  user: "bg-green-100 text-green-700"
};

// Status configurations with approval workflow
const STATUS_CONFIG = {
  to_do: { label: "To Do", color: "bg-slate-100 text-slate-700 border-slate-200", icon: Circle, order: 1 },
  doing: { label: "In Progress", color: "bg-blue-100 text-blue-700 border-blue-200", icon: Clock, order: 2 },
  in_progress: { label: "In Progress", color: "bg-blue-100 text-blue-700 border-blue-200", icon: Clock, order: 2 },
  done: { label: "Done", color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle, order: 3 },
  pending_approval: { label: "Pending Approval", color: "bg-amber-100 text-amber-700 border-amber-200", icon: Clock, order: 4 },
  review: { label: "Pending Approval", color: "bg-amber-100 text-amber-700 border-amber-200", icon: Clock, order: 4 },
  completed: { label: "Completed", color: "bg-green-100 text-green-700 border-green-200", icon: CheckCircle, order: 5 }
};

const PRIORITY_CONFIG = {
  urgent: { label: "Urgent", color: "bg-red-100 text-red-700 border-red-300", dotColor: "bg-red-500" },
  high: { label: "High", color: "bg-orange-100 text-orange-700 border-orange-300", dotColor: "bg-orange-500" },
  medium: { label: "Medium", color: "bg-yellow-100 text-yellow-700 border-yellow-300", dotColor: "bg-yellow-500" },
  low: { label: "Low", color: "bg-slate-100 text-slate-600 border-slate-300", dotColor: "bg-slate-400" }
};

const HEALTH_CONFIG = {
  on_track: { label: "On Track", color: "bg-emerald-100 text-emerald-700", dotColor: "bg-emerald-500" },
  at_risk: { label: "At Risk", color: "bg-amber-100 text-amber-700", dotColor: "bg-amber-500" },
  off_track: { label: "Off Track", color: "bg-red-100 text-red-700", dotColor: "bg-red-500" }
};

const Tasks = () => {
  const { businessId } = useBusiness();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  
  // View state
  const [viewMode, setViewMode] = useState("kanban"); // kanban, list, calendar
  // Services come from the Work Planner catalogue (shared across the CRM).
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState("all");
  const [servicesDialogOpen, setServicesDialogOpen] = useState(false);
  // Pre-filled due date ("yyyy-MM-dd'T'HH:mm") when a task is quick-created
  // by clicking an empty calendar slot — cleared whenever the dialog closes.
  const [quickCreateDate, setQuickCreateDate] = useState("");
  
  // Dialog states
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  // Comment states
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loadingComments, setLoadingComments] = useState(false);

  // Approval workflow states
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [taskToReject, setTaskToReject] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  
  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [healthFilter, setHealthFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  // Date filter — filters by due_date (falls back to created_at if the task
  // has no due date). Presets: today | yesterday | this_week | last_week |
  // this_month | last_month. "all" disables the filter. Defaults to the
  // current month — the planning cycle the Work Hub is organised around.
  const [dateFilter, setDateFilter] = useState("this_month");
  // Which field the date filter checks. Kept as state so we can add a UI
  // toggle later; default to due_date because "tasks due this week" is
  // the more common admin question.
  const [dateFilterField, setDateFilterField] = useState("due_date"); // "due_date" | "created_at"
  
  // Table sorting state
  const [sortConfig, setSortConfig] = useState({ key: "created_at", direction: "desc" });
  
  // Form states
  // Loading state for the extracted TaskFormBody's submit button. The form
  // state itself lives inside TaskFormBody now so keystrokes stay local.
  const [taskSubmitting, setTaskSubmitting] = useState(false);
  const [taskForm, setTaskForm] = useState({
    title: "",
    description: "",
    project_id: "",
    client_id: "",
    due_date: "",
    priority: "medium",
    health_status: "on_track",
    status: "to_do",
    is_urgent: false,
    assigned_users: []
  });
  
  // Clients list for dropdown
  const [clients, setClients] = useState([]);
  const [clientSearchQuery, setClientSearchQuery] = useState("");
  

  // Mobile viewport detection — admins on <768px get the redesigned Mobile CRM "Variation A"
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== "undefined" && window.innerWidth < 768
  );
  useEffect(() => {
    const onResize = () => setIsMobileViewport(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    fetchCurrentUser();
    fetchProjects();
    fetchServices();
    fetchTasks();
    fetchTeamMembers();
    fetchClients();
    // Keyed on the business ID only: the context hands out a fresh
    // selectedBusiness object on validation, which used to refetch the whole
    // (~2 MB) task list a second and third time.
  }, [businessId]);

  // Fetch clients for dropdown
  const fetchClients = async () => {
    try {
      const url = businessId
        ? `${BACKEND_URL}/api/clients?business_id=${businessId}`
        : `${BACKEND_URL}/api/clients`;
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  // Filter clients for searchable dropdown
  const filteredClients = clients.filter(client =>
    client.client_name?.toLowerCase().includes(clientSearchQuery.toLowerCase())
  );

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: "include",
      });
      if (response.ok) {
        const user = await response.json();
        setCurrentUser(user);
      }
    } catch (error) {
      console.error("Error fetching user:", error);
    }
  };

  const fetchServices = async () => {
    try {
      const q = businessId ? `?business_id=${businessId}` : "";
      const response = await fetch(`${BACKEND_URL}/api/work-planner/monthly/services${q}`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setServices((data.services || []).filter((x) => x.active !== false));
      }
    } catch (error) {
      console.error("Error fetching services:", error);
    }
  };

  const fetchProjects = async () => {
    try {
      let url = `${BACKEND_URL}/api/projects`;
      // Always scope to the current business when set (same isolation fix as fetchTasks).
      if (businessId) {
        url += `?business_id=${businessId}`;
      }
      const response = await fetch(url, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  // Only the newest task request may land: aborts the previous download (e.g.
  // the unscoped one fired before the business finished resolving) so a slow
  // stale response can't overwrite the current business's tasks.
  const tasksRequestRef = useRef(null);
  const fetchTasks = async () => {
    tasksRequestRef.current?.abort();
    const controller = new AbortController();
    tasksRequestRef.current = controller;
    try {
      setLoading(true);
      let url = `${BACKEND_URL}/api/tasks/enhanced`;
      const params = new URLSearchParams();
      // Always scope by the currently selected business when we have one.
      // Previous logic only sent this for role === "admin", which meant super_admin
      // fell through to the backend default of user.business_id (their home
      // business = Digital Nexus Media) — so switching to any other business in
      // the sidebar still showed DNM's tasks. Backend still enforces role gates.
      if (businessId) {
        params.append("business_id", businessId);
      }
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await fetch(url, {
        credentials: "include",
        signal: controller.signal,
      });
      if (response.ok) {
        const data = await response.json();
        if (tasksRequestRef.current === controller) setTasks(data);
      }
    } catch (error) {
      if (error.name !== "AbortError") console.error("Error fetching tasks:", error);
    } finally {
      if (tasksRequestRef.current === controller) setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/team/members`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setTeamMembers(data.filter(m => m.status === "approved"));
      }
    } catch (error) {
      console.error("Error fetching team members:", error);
    }
  };


  // Task CRUD
  // The Create / Edit form now lives in <TaskFormBody /> with its OWN local
  // state — keystrokes don't re-render this 1500-row parent any more. On
  // submit the form invokes the callback with the payload; the parent handles
  // the POST / PUT and refetches.
  const handleTaskSubmit = async (form) => {
    if (!form.title?.trim()) { toast.error("Task title is required"); return; }
    setTaskSubmitting(true);
    try {
      const payload = {
        ...form,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
        project_id: form.project_id || null,
        client_id: form.client_id || null,
        service: form.service || null,
      };
      const url = editingTask
        ? `${BACKEND_URL}/api/tasks/enhanced/${editingTask.task_id}`
        : `${BACKEND_URL}/api/tasks/enhanced`;
      const method = editingTask ? "PUT" : "POST";
      const response = await fetch(url, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        toast.success(editingTask ? "Task updated successfully" : "Task created successfully");
        setTaskDialogOpen(false);
        setEditingTask(null);
        fetchTasks();
        fetchProjects();
      } else {
        const error = await response.json();
        toast.error(error.detail || (editingTask ? "Failed to update task" : "Failed to create task"));
      }
    } catch (_) {
      toast.error(editingTask ? "Failed to update task" : "Failed to create task");
    } finally { setTaskSubmitting(false); }
  };

  const handleQuickStatusChange = async (taskId, newStatus) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tasks/enhanced/${taskId}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      
      if (response.ok) {
        const data = await response.json();
        const finalStatus = data.task?.status || newStatus;
        // Show appropriate message based on actual status
        if (finalStatus === "pending_approval") {
          toast.success("Task submitted for manager approval");
        } else {
          toast.success(`Task moved to ${STATUS_CONFIG[finalStatus]?.label || finalStatus}`);
        }
        fetchTasks();
        fetchProjects();
      }
    } catch (error) {
      toast.error("Failed to update task status");
    }
  };

  // Calendar view: drag-and-drop reschedule — same PUT the rest of the page
  // already uses for quick status changes, just updating due_date instead.
  const handleCalendarReschedule = async (task, newDateISOString) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tasks/enhanced/${task.task_id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ due_date: newDateISOString }),
      });
      if (response.ok) {
        toast.success(`"${task.title}" rescheduled to ${format(new Date(newDateISOString), "d MMM yyyy")}`);
        fetchTasks();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to reschedule task");
      }
    } catch (error) {
      toast.error("Failed to reschedule task");
    }
  };

  // Calendar view: clicking an empty day opens the existing Create Task
  // dialog with that date pre-filled, instead of a separate modal.
  const handleCalendarQuickCreate = (day) => {
    setEditingTask(null);
    setQuickCreateDate(format(day, "yyyy-MM-dd'T'HH:mm"));
    setTaskDialogOpen(true);
  };

  // Task Approval/Rejection handlers
  const handleApproveTask = async (taskId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tasks/${taskId}/approve`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "approve" }),
      });
      
      if (response.ok) {
        toast.success("Task approved successfully");
        fetchTasks();
        fetchProjects();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to approve task");
      }
    } catch (error) {
      toast.error("Failed to approve task");
    }
  };

  const handleRejectTask = async (taskId, reason) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tasks/${taskId}/approve`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "reject", comment: reason || "Needs more work" }),
      });
      
      if (response.ok) {
        toast.success("Task sent back for revision");
        fetchTasks();
        fetchProjects();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to reject task");
      }
    } catch (error) {
      toast.error("Failed to reject task");
    }
  };

  // Comment handlers
  const fetchComments = async (taskId) => {
    setLoadingComments(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/tasks/${taskId}/comments`, { credentials: "include" });
      if (res.ok) setComments(await res.json());
    } catch { /* ignore */ }
    finally { setLoadingComments(false); }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !editingTask) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/tasks/${editingTask.task_id}/comments`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ content: newComment }),
      });
      if (res.ok) { setNewComment(""); fetchComments(editingTask.task_id); toast.success("Comment added"); }
    } catch { toast.error("Failed to add comment"); }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/tasks/comments/${commentId}`, { method: "DELETE", credentials: "include" });
      if (res.ok && editingTask) { fetchComments(editingTask.task_id); toast.success("Comment deleted"); }
    } catch { toast.error("Failed to delete comment"); }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tasks/${taskId}`, {
        method: "DELETE",
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success("Task deleted successfully");
        fetchTasks();
        fetchProjects();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete task");
      }
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  const resetTaskForm = () => {
    setTaskForm({
      title: "",
      description: "",
      project_id: "",
      client_id: "",
      due_date: "",
      priority: "medium",
      health_status: "on_track",
      status: "to_do",
      is_urgent: false,
      assigned_users: []
    });
    setClientSearchQuery("");
  };

  const openEditTask = (task) => {
    setQuickCreateDate("");
    setEditingTask(task);
    setTaskForm({
      title: task.title || "",
      description: task.description || "",
      project_id: task.project_id || "",
      client_id: task.client_id || "",
      due_date: task.due_date ? format(new Date(task.due_date), "yyyy-MM-dd'T'HH:mm") : "",
      priority: task.priority || "medium",
      health_status: task.health_status || "on_track",
      status: task.status || "to_do",
      is_urgent: task.is_urgent || false,
      assigned_users: task.assigned_users || []
    });
    setClientSearchQuery("");
    setTaskDialogOpen(true);
    fetchComments(task.task_id);
  };


  // Filter tasks
  // Matches a task against a preset date filter using the configured field
  // (due_date by default, falling back to created_at). Monday-start weeks
  // matches Indian business convention.
  const matchesDateFilter = (task, filter, field) => {
    if (filter === "all") return true;
    const raw = task[field] || task.due_date || task.created_at;
    if (!raw) return false;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return false;
    const opts = { weekStartsOn: 1 };
    switch (filter) {
      case "today":      return isToday(d);
      case "yesterday":  return isYesterday(d);
      case "this_week":  return isThisWeek(d, opts);
      case "last_week":  return isSameWeek(d, subWeeks(new Date(), 1), opts);
      case "this_month": return isThisMonth(d);
      case "last_month": return isSameMonth(d, subMonths(new Date(), 1));
      default: return true;
    }
  };

  const serviceByKey = useMemo(() => Object.fromEntries(services.map((x) => [x.key, x])), [services]);
  // Service chip counts follow the date range so they match the list below.
  const tasksInRange = useMemo(
    () => (dateFilter === "all" ? tasks : tasks.filter((t) => matchesDateFilter(t, dateFilter, dateFilterField))),
    [tasks, dateFilter, dateFilterField]
  );
  const serviceCounts = useMemo(() => {
    const c = { none: 0 };
    for (const t of tasksInRange) { if (serviceByKey[t.service]) c[t.service] = (c[t.service] || 0) + 1; else c.none += 1; }
    return c;
  }, [tasksInRange, serviceByKey]);
  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      if (selectedService !== "all") {
        const has = !!serviceByKey[task.service];
        if (selectedService === "none" ? has : task.service !== selectedService) return false;
      }
      // Search filter
      if (searchQuery && !task.title.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      // Priority filter
      if (priorityFilter !== "all" && task.priority !== priorityFilter) {
        return false;
      }
      // Health filter
      if (healthFilter !== "all" && task.health_status !== healthFilter) {
        return false;
      }
      // Assignee filter
      if (assigneeFilter !== "all") {
        const assignees = task.assigned_users || [];
        if (!assignees.includes(assigneeFilter)) {
          return false;
        }
      }
      // Date preset filter
      if (dateFilter !== "all" && !matchesDateFilter(task, dateFilter, dateFilterField)) {
        return false;
      }
      return true;
    });
  }, [tasks, searchQuery, priorityFilter, healthFilter, assigneeFilter, dateFilter, dateFilterField, selectedService, serviceByKey]);

  // Sorted tasks for table view
  const sortedTasks = useMemo(() => {
    const sorted = [...filteredTasks];
    
    sorted.sort((a, b) => {
      // FIRST: Always put completed/done/pending_approval tasks at the bottom
      const completedStatuses = ["done", "pending_approval", "completed"];
      const aIsCompleted = completedStatuses.includes(a.status);
      const bIsCompleted = completedStatuses.includes(b.status);
      
      if (aIsCompleted && !bIsCompleted) return 1; // a goes to bottom
      if (!aIsCompleted && bIsCompleted) return -1; // b goes to bottom
      
      // If both are in same group (both completed or both active), apply normal sorting
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case "title":
          aValue = a.title?.toLowerCase() || "";
          bValue = b.title?.toLowerCase() || "";
          break;
        case "service":
          aValue = (serviceByKey[a.service]?.label || "").toLowerCase();
          bValue = (serviceByKey[b.service]?.label || "").toLowerCase();
          break;
        case "due_date":
          aValue = a.due_date ? new Date(a.due_date).getTime() : 0;
          bValue = b.due_date ? new Date(b.due_date).getTime() : 0;
          break;
        case "priority":
          const priorityOrder = { urgent: 1, high: 2, medium: 3, low: 4 };
          aValue = priorityOrder[a.priority] || 5;
          bValue = priorityOrder[b.priority] || 5;
          break;
        case "status":
          const statusOrder = { to_do: 1, doing: 2, done: 3, pending_approval: 4, completed: 5 };
          aValue = statusOrder[a.status] || 6;
          bValue = statusOrder[b.status] || 6;
          break;
        case "health_status":
          const healthOrder = { off_track: 1, at_risk: 2, on_track: 3 };
          aValue = healthOrder[a.health_status] || 4;
          bValue = healthOrder[b.health_status] || 4;
          break;
        case "created_at":
          aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
          bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        default:
          aValue = a[sortConfig.key] || "";
          bValue = b[sortConfig.key] || "";
      }
      
      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [filteredTasks, sortConfig]);

  // Handle column sorting
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  // Sort indicator component
  const SortIndicator = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-4 h-4 ml-1 opacity-30" />;
    }
    return sortConfig.direction === "asc" 
      ? <ArrowUp className="w-4 h-4 ml-1 text-indigo-600" />
      : <ArrowDown className="w-4 h-4 ml-1 text-indigo-600" />;
  };

  // Group tasks by status for Kanban (including new statuses)
  const tasksByStatus = useMemo(() => {
    const sortByLatest = (tasks) => [...tasks].sort((a, b) => new Date(b.updated_at || b.created_at || 0).getTime() - new Date(a.updated_at || a.created_at || 0).getTime());
    return {
      to_do: sortByLatest(filteredTasks.filter(t => t.status === "to_do" || t.status === "pending")),
      doing: sortByLatest(filteredTasks.filter(t => t.status === "doing" || t.status === "in_progress")),
      pending_approval: sortByLatest(filteredTasks.filter(t => t.status === "pending_approval" || t.status === "review")),
      completed: sortByLatest(filteredTasks.filter(t => t.status === "completed" || t.status === "done"))
    };
  }, [filteredTasks]);

  // Role-based permission checks
  const isAdmin = currentUser?.role === "admin";
  const isManager = currentUser?.role === "manager";
  const isProjectManager = currentUser?.role === "project_manager";
  const isTeamLeader = currentUser?.role === "team_leader";
  // Roles that can manage projects (create, edit, delete)
  const canManageProjects = isAdmin || isManager || isProjectManager;
  // Roles that can assign tasks to others
  const canAssignTasks = isAdmin || isManager || isProjectManager || isTeamLeader;
  // Roles that can approve/reject tasks
  const canApproveTask = isAdmin || isManager || isProjectManager;

  // Task Card Component
  const TaskCard = ({ task }) => {
    const dueDate = task.due_date ? new Date(task.due_date) : null;
    const isOverdue = dueDate && isPast(dueDate) && !["done", "completed", "pending_approval"].includes(task.status);
    const isDueToday = dueDate && isToday(dueDate);
    const isDueTomorrow = dueDate && isTomorrow(dueDate);
    const isPendingApproval = task.status === "pending_approval" || task.status === "review";
    const isCompleted = task.status === "completed" || task.status === "done";
    const isUrgent = task.is_urgent || task.priority === "urgent";

    return (
      <div
        data-testid={`task-card-${task.task_id}`}
        className={`bg-white dark:bg-slate-800 rounded-xl border p-4 mb-3 shadow-sm hover:shadow-md transition-all cursor-pointer ${
          isUrgent && !isCompleted ? "border-red-400 bg-red-50 dark:bg-red-900/20 ring-1 ring-red-200" :
          isPendingApproval ? "border-amber-300 bg-amber-50/50 dark:bg-amber-900/10" :
          isOverdue ? "border-red-300 bg-red-50/50 dark:bg-red-900/10" :
          isCompleted ? "border-green-200 bg-green-50/30 dark:bg-green-900/10" :
          "border-slate-200 dark:border-slate-700"
        }`}
        onClick={() => openEditTask(task)}
      >
        {/* Header: Priority dot + Title */}
        <div className="flex items-start gap-2 mb-2">
          <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${PRIORITY_CONFIG[task.priority]?.dotColor || "bg-slate-400"}`} />
          <h4 className={`font-medium flex-1 line-clamp-2 ${isCompleted ? "line-through text-slate-400" : "text-slate-900 dark:text-white"}`}>{task.title}</h4>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <button className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
                <MoreVertical className="w-4 h-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditTask(task); }}>
                <Edit className="w-4 h-4 mr-2" /> Edit
              </DropdownMenuItem>
              {!isCompleted && task.status !== "to_do" && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleQuickStatusChange(task.task_id, "to_do"); }}>
                  <Circle className="w-4 h-4 mr-2" /> Move to To Do
                </DropdownMenuItem>
              )}
              {!isCompleted && task.status !== "doing" && task.status !== "in_progress" && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleQuickStatusChange(task.task_id, "doing"); }}>
                  <Clock className="w-4 h-4 mr-2" /> Move to In Progress
                </DropdownMenuItem>
              )}
              {!isCompleted && !isPendingApproval && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleQuickStatusChange(task.task_id, "done"); }}>
                  <CheckCircle className="w-4 h-4 mr-2" /> Mark as Done
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                className="text-red-600"
                onClick={(e) => { e.stopPropagation(); setItemToDelete({ type: "task", id: task.task_id, name: task.title }); setDeleteDialogOpen(true); }}
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {/* Badges: Status (for pending/completed), Priority, Health */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {isUrgent && !isCompleted && (
            <Badge className="text-[10px] px-2 py-0.5 bg-red-500 text-white border-red-600 animate-pulse">
              <Flame className="w-3 h-3 mr-1" /> URGENT
            </Badge>
          )}
          {isPendingApproval && (
            <Badge className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-700 border-amber-300 animate-pulse">
              <Clock className="w-3 h-3 mr-1" /> Pending Approval
            </Badge>
          )}
          {isCompleted && (
            <Badge className="text-[10px] px-2 py-0.5 bg-green-100 text-green-700 border-green-300">
              <CheckCircle className="w-3 h-3 mr-1" /> Completed
            </Badge>
          )}
          <Badge className={`text-[10px] px-2 py-0.5 ${PRIORITY_CONFIG[task.priority]?.color || ""}`}>
            {PRIORITY_CONFIG[task.priority]?.label || task.priority}
          </Badge>
          <Badge className={`text-[10px] px-2 py-0.5 ${HEALTH_CONFIG[task.health_status]?.color || ""}`}>
            <span className={`w-1.5 h-1.5 rounded-full mr-1 ${HEALTH_CONFIG[task.health_status]?.dotColor || ""}`} />
            {HEALTH_CONFIG[task.health_status]?.label || task.health_status}
          </Badge>
          {serviceByKey[task.service] && (
            <Badge
              className="text-[10px] px-2 py-0.5"
              style={{ backgroundColor: `${serviceByKey[task.service].color}20`, color: serviceByKey[task.service].color, borderColor: serviceByKey[task.service].color }}
            >
              {serviceByKey[task.service].label}
            </Badge>
          )}
          {task.client && (
            <Badge className="text-[10px] px-2 py-0.5 bg-orange-100 text-orange-700 border-orange-200">
              <Briefcase className="w-2.5 h-2.5 mr-1" />
              {task.client.client_name}
            </Badge>
          )}
        </div>
        
        {/* Approval Actions for Managers */}
        {isPendingApproval && canApproveTask && (
          <div className="flex gap-2 mb-3" onClick={(e) => e.stopPropagation()}>
            <Button
              size="sm"
              className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs h-8"
              onClick={() => handleApproveTask(task.task_id)}
            >
              <CheckCircle className="w-3 h-3 mr-1" /> Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="flex-1 border-red-300 text-red-600 hover:bg-red-50 text-xs h-8"
              onClick={() => { setTaskToReject(task); setRejectDialogOpen(true); }}
            >
              <X className="w-3 h-3 mr-1" /> Reject
            </Button>
          </div>
        )}
        
        {/* Marked Done Info */}
        {isPendingApproval && task.marked_done_by_name && (
          <div className="text-xs text-amber-600 mb-2 flex items-center gap-1">
            <User className="w-3 h-3" />
            Submitted by {task.marked_done_by_name}
          </div>
        )}
        
        {/* Rejection Reason */}
        {task.rejection_reason && (task.status === "doing" || task.status === "in_progress") && (
          <div className="text-xs text-red-600 bg-red-50 p-2 rounded mb-2 flex items-start gap-1">
            <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>Feedback: {task.rejection_reason}</span>
          </div>
        )}
        
        {/* Due Date */}
        {dueDate && (
          <div className={`flex items-center gap-1.5 text-xs mb-2 ${
            isOverdue ? "text-red-600 font-medium" : isDueToday ? "text-amber-600" : "text-slate-500 dark:text-slate-400"
          }`}>
            <Calendar className="w-3.5 h-3.5" />
            <span>
              {isOverdue ? "Overdue: " : isDueToday ? "Today: " : isDueTomorrow ? "Tomorrow: " : ""}
              {format(dueDate, "MMM d, h:mm a")}
            </span>
          </div>
        )}
        
        {/* Assignees */}
        {task.assignees && task.assignees.length > 0 && (
          <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-slate-100 dark:border-slate-700">
            <Users className="w-3.5 h-3.5 text-slate-400" />
            <div className="flex -space-x-1">
              {task.assignees.slice(0, 3).map((assignee, idx) => (
                assignee.picture ? (
                  <img
                    key={assignee.user_id}
                    src={assignee.picture}
                    alt={assignee.name}
                    title={assignee.name}
                    className="w-6 h-6 rounded-full object-cover border-2 border-white dark:border-slate-800"
                  />
                ) : (
                  <div
                    key={assignee.user_id}
                    className="w-6 h-6 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center text-[10px] font-medium text-indigo-700 dark:text-indigo-300 border-2 border-white dark:border-slate-800"
                    title={assignee.name}
                  >
                    {assignee.name?.charAt(0).toUpperCase()}
                  </div>
                )
              ))}
              {task.assignees.length > 3 && (
                <div className="w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-[10px] font-medium text-slate-600 dark:text-slate-300 border-2 border-white dark:border-slate-800">
                  +{task.assignees.length - 3}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Latest Comment */}
        {task.latest_comment && (
          <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-700">
            <div className="flex items-start gap-1.5">
              <MessageSquare className="w-3 h-3 text-slate-400 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-2">
                  <span className="font-medium text-slate-600 dark:text-slate-300">{task.latest_comment.user_name}:</span>{" "}
                  {task.latest_comment.content}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Kanban Column Component
  const KanbanColumn = ({ status, tasks }) => {
    const config = STATUS_CONFIG[status];
    if (!config) return null;
    const IconComponent = config.icon;
    
    // Background colors for each status
    const bgColors = {
      to_do: "bg-slate-50 dark:bg-slate-900/50",
      doing: "bg-blue-50 dark:bg-blue-900/20",
      pending_approval: "bg-amber-50 dark:bg-amber-900/20",
      completed: "bg-green-50 dark:bg-green-900/20",
      done: "bg-emerald-50 dark:bg-emerald-900/20"
    };
    
    // Icon colors for each status
    const iconColors = {
      to_do: "text-slate-500",
      doing: "text-blue-500",
      pending_approval: "text-amber-500",
      completed: "text-green-500",
      done: "text-emerald-500"
    };
    
    return (
      <div className="flex-1 min-w-[280px] max-w-[350px]">
        <div className={`rounded-xl p-1 ${bgColors[status] || bgColors.to_do}`}>
          {/* Column Header */}
          <div className="flex items-center justify-between p-3 mb-2">
            <div className="flex items-center gap-2">
              <IconComponent className={`w-4 h-4 ${iconColors[status] || iconColors.to_do}`} />
              <span className="font-semibold text-slate-700 dark:text-slate-300">{config.label}</span>
              <span className="text-xs bg-white dark:bg-slate-800 px-2 py-0.5 rounded-full text-slate-500">
                {tasks.length}
              </span>
            </div>
            {status !== "pending_approval" && status !== "completed" && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  resetTaskForm();
                  setTaskForm(prev => ({ ...prev, status: status === "done" ? "to_do" : status }));
                  setTaskDialogOpen(true);
                }}
                className="h-7 w-7 p-0"
              >
                <Plus className="w-4 h-4" />
              </Button>
            )}
          </div>
          
          {/* Tasks */}
          <div className="px-2 pb-2 max-h-[calc(100vh-300px)] overflow-y-auto">
            {tasks.length === 0 ? (
              <div className="text-center py-8 text-sm text-slate-400">
                {status === "pending_approval" ? "No tasks awaiting approval" : 
                 status === "completed" ? "No completed tasks" : "No tasks"}
              </div>
            ) : (
              tasks.map(task => <TaskCard key={task.task_id} task={task} />)
            )}
          </div>
        </div>
      </div>
    );
  };

  // ════════ Mobile-native admin tasks view ════════
  // <768px AND admin/super_admin → render the "Variation A · Vibrant" mobile
  // design. The mobile component bundles its own Add Task sheet (POSTs to
  // /api/tasks/enhanced) so the desktop create-dialog plumbing isn't needed.
  if (isMobileViewport && (currentUser?.role === "admin" || currentUser?.role === "super_admin")) {
    return (
      <MobileTasksA
        tasks={tasks}
        loading={loading}
        currentUser={currentUser}
        teamMembers={teamMembers}
        projects={projects}
        businessId={businessId}
        onToggleDone={(task, isDone) => handleQuickStatusChange(task.task_id, isDone ? "to_do" : "done")}
        onTaskCreated={() => fetchTasks()}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Tasks</h1>
          <p className="text-slate-600 dark:text-slate-400">Manage tasks by service, client and team</p>
        </div>
        <div className="flex items-center gap-2">
          {canManageProjects && (
            <Button variant="outline" onClick={() => setServicesDialogOpen(true)} data-testid="manage-services-button" className="rounded-full">
              <Layers className="w-4 h-4 mr-2" />
              Manage Services
            </Button>
          )}
          <Dialog open={taskDialogOpen} onOpenChange={(open) => {
            setTaskDialogOpen(open);
            if (!open) {
              setEditingTask(null);
              setQuickCreateDate("");
              resetTaskForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button data-testid="new-task-button" onClick={() => setQuickCreateDate("")} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full">
                <Plus className="w-4 h-4 mr-2" />
                New Task
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white dark:bg-slate-800 rounded-2xl max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-xl font-bold">
                  <CheckCircle className="w-5 h-5 text-indigo-600" />
                  {editingTask ? "Edit Task" : "Create Task"}
                </DialogTitle>
              </DialogHeader>
              <div>
                {/* Extracted form body — owns its own state so keystrokes don't
                    re-render the 1500+ task rows behind it. */}
                <TaskFormBody
                  key={editingTask?.task_id || quickCreateDate || "new"}
                  editingTask={editingTask}
                  services={services}
                  teamMembers={teamMembers}
                  clients={clients}
                  initialService={selectedService !== "all" && selectedService !== "none" ? selectedService : ""}
                  initialDueDate={quickCreateDate}
                  statusConfig={STATUS_CONFIG}
                  priorityConfig={PRIORITY_CONFIG}
                  healthConfig={HEALTH_CONFIG}
                  roleColors={ROLE_COLORS}
                  roleLabels={ROLE_LABELS}
                  onSubmit={handleTaskSubmit}
                  submitting={taskSubmitting}
                />

                {/* Comments Section - only when editing */}
                {editingTask && (
                  <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
                    <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2 mb-3">
                      <MessageSquare className="w-4 h-4 text-indigo-500" />
                      Comments ({comments.length})
                    </h3>
                    {/* Add comment */}
                    <div className="flex gap-2 mb-4">
                      <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center shrink-0 mt-0.5">
                        {currentUser?.picture ? (
                          <img src={currentUser.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                        ) : (
                          <span className="text-xs font-medium text-indigo-700">{currentUser?.name?.charAt(0)?.toUpperCase() || "U"}</span>
                        )}
                      </div>
                      <div className="flex-1 flex gap-2">
                        <Input
                          placeholder="Add a comment..."
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleAddComment()}
                          className="flex-1 text-sm"
                        />
                        <Button size="sm" onClick={handleAddComment} disabled={!newComment.trim()} className="bg-indigo-600 hover:bg-indigo-700 px-3">
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {/* Comments list */}
                    {loadingComments ? (
                      <p className="text-center text-slate-400 text-sm py-4">Loading...</p>
                    ) : comments.length === 0 ? (
                      <p className="text-center text-slate-400 text-sm py-4">No comments yet</p>
                    ) : (
                      <div className="space-y-3 max-h-[250px] overflow-y-auto">
                        {comments.map((c) => (
                          <div key={c._id || c.comment_id} className="flex gap-2 group">
                            <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                              {c.user_picture ? (
                                <img src={c.user_picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                              ) : (
                                <span className="text-xs font-medium text-slate-600">{(c.user_name || c.created_by_name || "U").charAt(0).toUpperCase()}</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-slate-700 dark:text-slate-300">{c.user_name || c.created_by_name || "Unknown"}</span>
                                <div className="flex items-center gap-1">
                                  <span className="text-[10px] text-slate-400">{c.created_at ? format(new Date(c.created_at), "MMM d, h:mm a") : ""}</span>
                                  <button onClick={() => handleDeleteComment(c._id || c.comment_id)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500 transition-all">
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                              <p className="text-sm text-slate-600 dark:text-slate-400 mt-0.5">{c.text || c.content || c.comment}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Services Filter Section — same catalogue as Work Hub › Work Planner */}
      <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700" data-testid="services-filter">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
            <Layers className="w-4 h-4 text-indigo-500" />
            Services
          </h3>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {services.length} service{services.length !== 1 ? "s" : ""} · synced with Work Planner
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedService("all")}
            data-testid="service-filter-all"
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all ${
              selectedService === "all"
                ? "bg-indigo-600 text-white shadow-md"
                : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
            }`}
          >
            <LayoutGrid className="w-3.5 h-3.5" />
            <span>All Tasks</span>
            <Badge variant="secondary" className={`ml-1 text-[10px] px-1.5 py-0 ${selectedService === "all" ? "bg-white/20 text-white" : "bg-slate-200 dark:bg-slate-600"}`}>
              {tasksInRange.length}
            </Badge>
          </button>
          {services.map((svc) => {
            const on = selectedService === svc.key;
            return (
              <button
                key={svc.key}
                onClick={() => setSelectedService(on ? "all" : svc.key)}
                data-testid={`service-filter-${svc.key}`}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all ${
                  on ? "text-white shadow-md" : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
                }`}
                style={on ? { backgroundColor: svc.color } : {}}
              >
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: on ? "white" : svc.color }} />
                <span className="truncate max-w-[140px] sm:max-w-[170px]">{svc.label}</span>
                <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 flex-shrink-0 ${on ? "bg-white/20 text-white" : "bg-slate-200 dark:bg-slate-600"}`}>
                  {serviceCounts[svc.key] || 0}
                </Badge>
              </button>
            );
          })}
          <button
            onClick={() => setSelectedService(selectedService === "none" ? "all" : "none")}
            data-testid="service-filter-none"
            title="Tasks without a service"
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all ${
              selectedService === "none" ? "bg-slate-700 text-white shadow-md" : "border border-dashed border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700"
            }`}
          >
            <span>No service</span>
            <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 ${selectedService === "none" ? "bg-white/20 text-white" : "bg-slate-100 dark:bg-slate-600"}`}>
              {serviceCounts.none || 0}
            </Badge>
          </button>
          {services.length === 0 && (
            <p className="text-sm text-slate-500 dark:text-slate-400 py-1">No services yet. {canManageProjects && "Add them with Manage Services."}</p>
          )}
        </div>
      </div>

      {/* Filters & View Toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search tasks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-48"
            />
          </div>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-32">
              <Flag className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={healthFilter} onValueChange={setHealthFilter}>
            <SelectTrigger className="w-32">
              <Target className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Health" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {Object.entries(HEALTH_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
            <SelectTrigger className="w-36">
              <User className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Assignee" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Assignees</SelectItem>
              {teamMembers.map(member => (
                <SelectItem key={member.user_id} value={member.user_id}>{member.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Date preset filter (Today / Yesterday / This-Last week / This-Last month) */}
          <Select value={dateFilter} onValueChange={setDateFilter}>
            <SelectTrigger className="w-40" data-testid="task-date-filter">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Date" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Dates</SelectItem>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="yesterday">Yesterday</SelectItem>
              <SelectItem value="this_week">This week</SelectItem>
              <SelectItem value="last_week">Last week</SelectItem>
              <SelectItem value="this_month">This month</SelectItem>
              <SelectItem value="last_month">Last month</SelectItem>
              <div className="border-t border-slate-100 dark:border-slate-800 mt-1 pt-1 px-2 pb-1">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Filter by</div>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDateFilterField("due_date"); }}
                    className={`flex-1 text-[11px] font-semibold px-2 py-1 rounded ${dateFilterField === "due_date" ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300" : "text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800"}`}
                  >
                    Due date
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDateFilterField("created_at"); }}
                    className={`flex-1 text-[11px] font-semibold px-2 py-1 rounded ${dateFilterField === "created_at" ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300" : "text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800"}`}
                  >
                    Created
                  </button>
                </div>
              </div>
            </SelectContent>
          </Select>
        </div>
        
        {/* View Toggle */}
        <Tabs value={viewMode} onValueChange={setViewMode} className="w-auto">
          <TabsList>
            <TabsTrigger value="kanban" data-testid="kanban-view-tab" className="flex items-center gap-1">
              <LayoutGrid className="w-4 h-4" />
              <span className="hidden sm:inline">Kanban</span>
            </TabsTrigger>
            <TabsTrigger value="list" data-testid="table-view-tab" className="flex items-center gap-1">
              <List className="w-4 h-4" />
              <span className="hidden sm:inline">Table</span>
            </TabsTrigger>
            <TabsTrigger value="calendar" data-testid="calendar-view-tab" className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <span className="hidden sm:inline">Calendar</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Task Views */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin w-8 h-8 border-3 border-indigo-200 border-t-indigo-600 rounded-full" />
        </div>
      ) : viewMode === "kanban" ? (
        /* Kanban View with Approval Workflow */
        <div className="flex gap-4 overflow-x-auto pb-4">
          <KanbanColumn status="to_do" tasks={tasksByStatus.to_do} />
          <KanbanColumn status="doing" tasks={tasksByStatus.doing} />
          <KanbanColumn status="pending_approval" tasks={tasksByStatus.pending_approval} />
          <KanbanColumn status="completed" tasks={tasksByStatus.completed} />
        </div>
      ) : viewMode === "calendar" ? (
        <TasksCalendarView
          tasks={filteredTasks}
          priorityConfig={PRIORITY_CONFIG}
          onTaskClick={openEditTask}
          onReschedule={handleCalendarReschedule}
          onQuickCreate={handleCalendarQuickCreate}
          serviceByKey={serviceByKey}
        />
      ) : (
        /* Table View */
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          {sortedTasks.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No tasks found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-700/50">
                    <TableHead className="w-12 text-center">S.No.</TableHead>
                    <TableHead className="w-12 text-center">
                      <Checkbox
                        checked={sortedTasks.filter(t => t.status !== "done").length === 0 && sortedTasks.length > 0}
                        disabled
                        className="opacity-50"
                      />
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600/50 min-w-[200px]"
                      onClick={() => handleSort("title")}
                    >
                      <div className="flex items-center">
                        Task Name
                        <SortIndicator columnKey="title" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600/50 min-w-[150px]"
                      onClick={() => handleSort("service")}
                    >
                      <div className="flex items-center">
                        Service
                        <SortIndicator columnKey="service" />
                      </div>
                    </TableHead>
                    <TableHead className="min-w-[130px]">
                      Client
                    </TableHead>
                    <TableHead className="min-w-[120px]">
                      Assigned To
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600/50 min-w-[110px]"
                      onClick={() => handleSort("due_date")}
                    >
                      <div className="flex items-center">
                        Due Date
                        <SortIndicator columnKey="due_date" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600/50 min-w-[100px]"
                      onClick={() => handleSort("priority")}
                    >
                      <div className="flex items-center">
                        Priority
                        <SortIndicator columnKey="priority" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600/50 min-w-[110px]"
                      onClick={() => handleSort("status")}
                    >
                      <div className="flex items-center">
                        Status
                        <SortIndicator columnKey="status" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600/50 min-w-[100px]"
                      onClick={() => handleSort("health_status")}
                    >
                      <div className="flex items-center">
                        Health
                        <SortIndicator columnKey="health_status" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600/50 min-w-[100px]"
                      onClick={() => handleSort("created_at")}
                    >
                      <div className="flex items-center">
                        Created
                        <SortIndicator columnKey="created_at" />
                      </div>
                    </TableHead>
                    <TableHead className="w-16 text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedTasks.map((task, index) => {
                    const dueDate = task.due_date ? new Date(task.due_date) : null;
                    const isOverdue = dueDate && isPast(dueDate) && task.status !== "done";
                    const createdDate = task.created_at ? new Date(task.created_at) : null;
                    
                    return (
                      <TableRow
                        key={task.task_id}
                        data-testid={`task-row-${task.task_id}`}
                        className={`hover:bg-slate-50 dark:hover:bg-slate-700/50 ${
                          isOverdue ? "bg-red-50/50 dark:bg-red-900/10" : ""
                        }`}
                      >
                        {/* Serial Number */}
                        <TableCell className="text-center text-sm font-medium text-slate-500 dark:text-slate-400">
                          {index + 1}
                        </TableCell>
                        
                        {/* Status Checkbox */}
                        <TableCell className="text-center">
                          <Checkbox
                            checked={task.status === "done"}
                            onCheckedChange={(checked) => {
                              handleQuickStatusChange(task.task_id, checked ? "done" : "to_do");
                            }}
                          />
                        </TableCell>
                        
                        {/* Task Name */}
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${PRIORITY_CONFIG[task.priority]?.dotColor || "bg-slate-400"}`} />
                            <span className={`font-medium ${task.status === "done" ? "line-through text-slate-400" : "text-slate-900 dark:text-white"}`}>
                              {task.title}
                            </span>
                          </div>
                          {task.description && (
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-1">{task.description}</p>
                          )}
                        </TableCell>
                        
                        {/* Service */}
                        <TableCell>
                          {serviceByKey[task.service] ? (
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: serviceByKey[task.service].color }} />
                              <span className="text-sm text-slate-700 dark:text-slate-300">{serviceByKey[task.service].label}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">No service</span>
                          )}
                        </TableCell>
                        
                        {/* Client */}
                        <TableCell>
                          {task.client ? (
                            <div className="flex items-center gap-2">
                              <Briefcase className="w-3 h-3 text-orange-500 flex-shrink-0" />
                              <span className="text-sm text-slate-700 dark:text-slate-300">{task.client.client_name}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">-</span>
                          )}
                        </TableCell>
                        
                        {/* Assignees */}
                        <TableCell>
                          {task.assignees && task.assignees.length > 0 ? (
                            <div className="flex items-center gap-1">
                              <div className="flex -space-x-1">
                                {task.assignees.slice(0, 3).map(assignee => (
                                  <div
                                    key={assignee.user_id}
                                    className="w-7 h-7 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center text-xs font-medium text-indigo-700 dark:text-indigo-300 border-2 border-white dark:border-slate-800"
                                    title={assignee.name}
                                  >
                                    {assignee.name?.charAt(0).toUpperCase()}
                                  </div>
                                ))}
                              </div>
                              {task.assignees.length > 3 && (
                                <span className="text-xs text-slate-500">+{task.assignees.length - 3}</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">Unassigned</span>
                          )}
                        </TableCell>
                        
                        {/* Due Date */}
                        <TableCell>
                          {dueDate ? (
                            <span className={`text-sm flex items-center gap-1 ${isOverdue ? "text-red-600 font-medium" : "text-slate-700 dark:text-slate-300"}`}>
                              <Calendar className="w-3 h-3" />
                              {format(dueDate, "MMM d, yyyy")}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">No due date</span>
                          )}
                        </TableCell>
                        
                        {/* Priority */}
                        <TableCell>
                          <Badge className={`text-xs ${PRIORITY_CONFIG[task.priority]?.color || ""}`}>
                            {PRIORITY_CONFIG[task.priority]?.label || task.priority}
                          </Badge>
                        </TableCell>
                        
                        {/* Status */}
                        <TableCell>
                          {task.status === "pending_approval" ? (
                            <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-300 animate-pulse">
                              Pending Approval
                            </Badge>
                          ) : task.status === "completed" ? (
                            <Badge className="text-xs bg-green-100 text-green-700 border-green-300">
                              Completed
                            </Badge>
                          ) : (
                            <Select
                              value={task.status}
                              onValueChange={(value) => handleQuickStatusChange(task.task_id, value)}
                            >
                              <SelectTrigger className="h-7 text-xs w-[100px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="to_do">To Do</SelectItem>
                                <SelectItem value="doing">In Progress</SelectItem>
                                <SelectItem value="done">Done</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </TableCell>
                        
                        {/* Health Status */}
                        <TableCell>
                          <Badge className={`text-xs ${HEALTH_CONFIG[task.health_status]?.color || ""}`}>
                            <span className={`w-1.5 h-1.5 rounded-full mr-1.5 inline-block ${HEALTH_CONFIG[task.health_status]?.dotColor || ""}`} />
                            {HEALTH_CONFIG[task.health_status]?.label || task.health_status}
                          </Badge>
                        </TableCell>
                        
                        {/* Created Date */}
                        <TableCell>
                          {createdDate ? (
                            <span className="text-sm text-slate-500 dark:text-slate-400">
                              {format(createdDate, "MMM d")}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">-</span>
                          )}
                        </TableCell>
                        
                        {/* Actions */}
                        <TableCell className="text-center">
                          {/* Approval Actions for pending tasks */}
                          {task.status === "pending_approval" && canApproveTask ? (
                            <div className="flex gap-1 justify-center">
                              <Button
                                size="sm"
                                className="h-7 px-2 bg-green-600 hover:bg-green-700 text-white text-xs"
                                onClick={() => handleApproveTask(task.task_id)}
                              >
                                <ThumbsUp className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 border-red-300 text-red-600 hover:bg-red-50 text-xs"
                                onClick={() => { setTaskToReject(task); setRejectDialogOpen(true); }}
                              >
                                <ThumbsDown className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
                                  <MoreVertical className="w-4 h-4 text-slate-400" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEditTask(task)}>
                                  <Eye className="w-4 h-4 mr-2" /> View / Edit
                                </DropdownMenuItem>
                                {task.status !== "completed" && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleQuickStatusChange(task.task_id, "to_do")}>
                                      <Circle className="w-4 h-4 mr-2" /> Set To Do
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleQuickStatusChange(task.task_id, "doing")}>
                                      <Clock className="w-4 h-4 mr-2" /> Set In Progress
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleQuickStatusChange(task.task_id, "done")}>
                                      <CheckCircle className="w-4 h-4 mr-2" /> Mark as Done
                                    </DropdownMenuItem>
                                  </>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  className="text-red-600"
                                  onClick={() => { setItemToDelete({ type: "task", id: task.task_id, name: task.title }); setDeleteDialogOpen(true); }}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
          
          {/* Table Footer with count */}
          {sortedTasks.length > 0 && (
            <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 text-sm text-slate-500 dark:text-slate-400 bg-slate-50/50 dark:bg-slate-700/30">
              Showing {sortedTasks.length} task{sortedTasks.length !== 1 ? "s" : ""}
              {sortConfig.key && (
                <span className="ml-2">
                  • Sorted by {sortConfig.key.replace("_", " ")} ({sortConfig.direction === "asc" ? "ascending" : "descending"})
                </span>
              )}
            </div>
          )}
        </div>
      )}

      <ServicesDialog open={servicesDialogOpen} onClose={() => setServicesDialogOpen(false)} businessId={businessId} onChanged={fetchServices} />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{itemToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                handleDeleteTask(itemToDelete.id);
                setDeleteDialogOpen(false);
                setItemToDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Task Rejection Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={(open) => {
        setRejectDialogOpen(open);
        if (!open) {
          setTaskToReject(null);
          setRejectionReason("");
        }
      }}>
        <DialogContent className="bg-white dark:bg-slate-800 rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl font-bold text-red-600">
              <ThumbsDown className="w-5 h-5" />
              Reject Task
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-slate-50 dark:bg-slate-700/50 p-3 rounded-lg">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{taskToReject?.title}</p>
              {taskToReject?.marked_done_by_name && (
                <p className="text-xs text-slate-500 mt-1">Submitted by {taskToReject.marked_done_by_name}</p>
              )}
            </div>
            <div>
              <Label>Feedback / Reason for Rejection *</Label>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Explain why this task needs more work..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => {
                if (!rejectionReason.trim()) {
                  toast.error("Please provide a reason for rejection");
                  return;
                }
                handleRejectTask(taskToReject.task_id, rejectionReason);
                setRejectDialogOpen(false);
                setTaskToReject(null);
                setRejectionReason("");
              }}
            >
              <ThumbsDown className="w-4 h-4 mr-2" />
              Reject & Send Back
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Tasks;
